import { useId } from 'react';

import { Field } from '../../components/ui/Field';
import { Select } from '../../components/ui/form';
import { KEY_OPTIONS, MAX_BPM, MIN_BPM } from './keys';

/**
 * Tempo and key, as one row, in both forms that ask for them.
 *
 * ## Asked rather than detected
 *
 * Nothing on the platform measures either. The transcoder reads duration and
 * loudness with ffmpeg, which has no beat tracker and no key detector - adding
 * one means a native dependency in the worker and an answer that is right about
 * four times in five. The artist already knows, so the artist is asked, and
 * what somebody typed beats a guess presented as a fact.
 *
 * ## Both optional, and said so
 *
 * Plenty of music has no meaningful BPM and plenty of artists have no idea what
 * key theirs is in. A required field here would produce a column full of
 * confident nonsense, which is worse for the mixing this exists to enable than
 * a column full of nulls.
 *
 * One component rather than two copies because the bounds and the wording have
 * to match between uploading and editing - the way a pair like this goes wrong
 * is one side being changed and the other not.
 */
export function TempoAndKey({
  bpm,
  keySignature,
  disabled = false,
  onBpm,
  onKey,
}: {
  /** Held as a string so the field can be empty, which is a real answer. */
  bpm: string;
  keySignature: string;
  disabled?: boolean;
  onBpm: (value: string) => void;
  onKey: (value: string) => void;
}) {
  const keyId = useId();

  const typed = bpm.trim();
  const number = Number(typed);

  /*
   * Checked here as well as on the server.
   *
   * Not because the server's check can be skipped - it cannot - but because a
   * 422 arriving after the upload button is a poor way to learn you typed the
   * year instead of the tempo.
   */
  const outOfRange =
    typed !== '' && (!Number.isFinite(number) || number < MIN_BPM || number > MAX_BPM);

  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <Field
        label="BPM"
        name="bpm"
        type="number"
        inputMode="numeric"
        min={MIN_BPM}
        max={MAX_BPM}
        step={1}
        value={bpm}
        disabled={disabled}
        data-track-bpm
        placeholder="Optional"
        error={outOfRange ? `Between ${MIN_BPM} and ${MAX_BPM}.` : undefined}
        onChange={(event) => onBpm(event.target.value)}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor={keyId} className="text-sm font-medium text-soft">
          Key
        </label>
        <Select
          id={keyId}
          value={keySignature}
          disabled={disabled}
          data-track-key
          placeholder="Optional"
          options={KEY_OPTIONS}
          onChange={onKey}
        />
      </div>
    </div>
  );
}

/**
 * The field's text, as the API wants it.
 *
 * Empty means "no answer", which is null rather than zero - a track nobody has
 * given a tempo for is not a track at 0 BPM, and the difference matters to
 * anything that later tries to mix out of it.
 */
export function bpmForRequest(bpm: string): number | null {
  const typed = bpm.trim();

  return typed === '' ? null : Number(typed);
}
